import { zodFunction } from '@/utils/zod-utils';
import {
	ChartSummary,
	createChartSchema,
	updateChartStyleOptionsSchema,
	updateChartTitleSchema,
	updateChartTypeSchema,
	ExtendedChartWidgetProps,
	updateChartTypeOfMeasuresSchema,
	updateChartSortingSchema,
	updateTrendAndForecastSchema,
	updateOtherChartDataOptionsSchema,
	updateChartQuerySchema,
	BuildChartContext,
	buildChartSchema,
	ChartContextPromptEntry,
} from './types';
import { translateChartToJSON } from '@sisense/sdk-ui/analytics-composer';
import { createChart } from './create-chart/create-chart';
import { updateChartTitle } from './update-chart/update-chart-title';
import { updateChartType } from './update-chart/update-chart-type';
import {
	updateChartTypeOfMeasures,
	updateChartSorting,
	updateTrendAndForecast,
	updateOtherChartDataOptions,
} from './update-chart/update-chart-data-options';
import { updateChartStyleOptions } from './update-chart/update-chart-style-options';
import { updateChartQuery } from './update-chart/update-chart-query';
import {
	TOOL_CONTEXT_MAX_ASSETS,
	TOOL_NAME_BUILD_CHART,
	toEngineName,
} from '@/constants';
import { devDebug } from '@/utils/dev-debug';
import { getBaseChartSummary } from './build-chart-service';
import {
	datasetFromSavedChartWidget,
	queryStateFromSavedChartWidget,
} from './dataset-from-saved-chart-widget';
import z from 'zod';
import { createToolsCompletion } from '@/utils/chat-completion-utils';

/**
 * Sanitizes user-provided strings before embedding them in prompt literals.
 * Escapes backticks and backslashes to prevent prompt structure breakage.
 */
function sanitizePromptLiteral(value: string | undefined): string {
	if (!value) return '';
	return value
		.replace(/\\/g, '\\\\')
		.replace(/`/g, '\\`')
		.replace(/\r?\n/g, ' ');
}

/**
 * Higher-order function to create a tool with bound context
 * @param originalSchema - The schema for the tool
 * @param name - The name of the tool
 * @param originalFunction - The function to call when the tool is used
 * @param boundContext - The context for the build chart
 * @returns A zod function for the tool
 */
const createChartTool = (
	originalSchema: any,
	name: string,
	description: string,
	originalFunction: any,
	boundContext: BuildChartContext,
) => {
	return zodFunction({
		schema: originalSchema,
		name,
		description,
		function: async (params: any, context: any) => {
			// Merge LLM params with bound context params and dependencies
			return originalFunction(
				{ ...params },
				{ ...boundContext, ...context },
			);
		},
	});
};

/**
 * Create a set of tools for the build chart with injected dependencies
 * @param context - The bound context for the build chart.
 * @returns A set of tools for the build chart.
 */
const createChartTools = (context: BuildChartContext) => {
	return [
		createChartTool(
			createChartSchema,
			'createChart',
			`Create a new chart from user prompt. Use this tool for **initial chart creation only** (when no existing chart is being revised).

IMPORTANT: Split the user prompt into two parts:
1. queryPrompt: Be as faithful to the original user prompt as possible, but remove chart-specific instructions (e.g., "bar chart", "use X as dimension"). Focus on what data to retrieve - the tool will extract specific dimensions, measures, and filters automatically.
2. chartPrompt: Be as faithful to the original user prompt as possible and keep chart-specific visualization instructions. If the user specifies a chart type (e.g. "bar chart"), pass it via the \`chartType\` field instead of embedding it here.`,
			createChart,
			context,
		),

		createChartTool(
			updateChartQuerySchema,
			'updateChartQuery',
			`Revise the data structure of an existing chart: add/remove dimensions, measures, or filters, swap columns (e.g. Revenue → Profit), or apply data limits (Top 10, Bottom 5).
Preserves the chart's existing style options (chart type, legend, axes) in the result.
Use this instead of createChart when the user is changing WHAT DATA an existing chart shows.`,
			updateChartQuery,
			context,
		),

		createChartTool(
			updateChartTitleSchema,
			'updateChartTitle',
			'Update the title of an existing chart',
			updateChartTitle,
			context,
		),

		createChartTool(
			updateChartTypeSchema,
			'updateChartType',
			'Switch the chart type of an existing chart',
			updateChartType,
			context,
		),

		createChartTool(
			updateChartTypeOfMeasuresSchema,
			'updateChartTypeOfMeasures',
			`Update the chart type of specific measures within a mixed/combo chart (e.g., make one measure a Bar and another a Line).`,
			updateChartTypeOfMeasures,
			context,
		),

		createChartTool(
			updateChartSortingSchema,
			'updateChartSorting',
			`Update the sorting order (Ascending/Descending) of an existing chart.

IMPORTANT: This tool ONLY handles sorting order. For data limits (Top 10, Bottom 5), use updateChartQuery instead.`,
			updateChartSorting,
			context,
		),

		createChartTool(
			updateTrendAndForecastSchema,
			'updateTrendAndForecast',
			`Update the trend and forecast analysis of existing measures in an existing chart`,
			updateTrendAndForecast,
			context,
		),

		createChartTool(
			updateOtherChartDataOptionsSchema,
			'updateOtherChartDataOptions',
			`Update the visual properties of an existing chart's axes and per-measure options (e.g. axis assignments, colors, right-axis toggle).

For a measure, you can update its color or show it on the right Y axis.

IMPORTANT: This tool cannot swap data columns (e.g. changing "Revenue" to "Profit"). Use updateChartQuery for that.`,
			updateOtherChartDataOptions,
			context,
		),

		createChartTool(
			updateChartStyleOptionsSchema,
			'updateChartStyleOptions',
			`Update cosmetic styling options: legend position, axis titles (text), axis label formatting.`,
			updateChartStyleOptions,
			context,
		),
	];
};

/**
 * Build contextual system prompt for build chart
 */
export function buildContextualSystemPrompt(
	chartsForContext: ChartContextPromptEntry[],
	priorChartId?: string,
	outerQueryId?: string,
) {
	const sanitizedPriorChartId = sanitizePromptLiteral(priorChartId);
	const sanitizedOuterQueryId = sanitizePromptLiteral(outerQueryId);

	const revisionBlock =
		priorChartId !== undefined && priorChartId !== ''
			? `You are revising an existing chart (ID: ${sanitizedPriorChartId}).
When calling any update tool (updateChartQuery, updateChartSorting,
updateChartStyleOptions, etc.), use ${sanitizedPriorChartId} as the chartId argument.

`
			: '';

	const chartScopeInstruction =
		priorChartId !== undefined && priorChartId !== ''
			? `You are revising chart \`${sanitizedPriorChartId}\`. Its full configuration is provided in the context below.`
			: 'Use the last chart in the context as the current chart unless the user prompt specifies otherwise.';

	const outerQueryInstruction =
		outerQueryId != null && outerQueryId !== ''
			? `**Outer queryId (verbatim):** The caller requires verbatim reuse of prior query result id \`${sanitizedOuterQueryId}\`. When you call \`createChart\`, pass that exact string as the \`queryId\` argument.\n\n`
			: '';

	return `You are an intelligent and proactive chart assistant. 
Your role is to create and update a chart based on natural language requests by using the available tools.

${revisionBlock}${outerQueryInstruction}

IMPORTANT INSTRUCTIONS:
1. Use the charts and their revisions in the following context to build the chart: ${JSON.stringify(
		{
			charts: chartsForContext,
		},
		null,
		2,
	)}
2. Always reference the charts by their IDs instead of their titles.

3. ${chartScopeInstruction}

4. If the user prompt involves multiple actions that require different available tools (e.g., sorting, trend and forecast, chart type of measures, and other data options),
split it into multiple prompts. For example, if the user prompt is "create a new chart showing total revenue by brand, and sort by revenue in descending order",
split it into two prompts: "create a new chart showing total revenue by brand" and "sort by revenue in descending order" in step by step.
To add a filter to an existing chart and sort it: (1) \`updateChartQuery\` for the filter, then (2) \`updateChartSorting\` on the resulting chart.
If the prompt can be handled by a single tool, use that tool without splitting the prompt into multiple steps.

5. When responding to changes involving an existing chart:
  - If the prompt involves **adding or removing** chart's data structure such as dimensions, measures, or filters, or **swapping columns** (e.g., "add revenue", "remove date", "filter by region", "swap Revenue for Profit"), or **data limits** (Top 10, Bottom 5), use **\`updateChartQuery(revisionPrompt, chartId)\`**. This automatically preserves the existing chart's style options (chart type, legend, axes).
  - If the prompt involves **enhancing** how existing fields are displayed (e.g., "sort by revenue in descending order", "show cost on the right axis", "add trend line", "add forecast line", "remove trend line", "remove forecast line"), apply the changes **to the existing chart** in step by step.

6. When updating the chart type of existing measures, re-apply the data options and style options changes to the new chart.

7. Chart title: The chart title is automatically set from the data API during chart creation — do NOT call 'updateChartTitle' as part of initial chart creation or data modifications. Only call 'updateChartTitle' when the user explicitly asks to rename or change the title (e.g., "rename the chart to X", "change the title to Y").

8. After \`createChart\` or \`updateChartQuery\`, read \`dataset\` (sample rows) and \`chartState\`. If something is clearly wrong for the task (encoding, scale, clutter), fix it with the right tool.
  - **Scales and measures:** With two+ numeric measures, prefer a **single shared Y** when values are commensurate and both series stay readable on one scale. Use **dual Y** when magnitudes differ greatly or one series would be flat/crushed on a shared axis. Apply via \`updateOtherChartDataOptions\` (\`showOnRightAxis\` on the right-hand measure) then \`updateChartStyleOptions\` (\`yAxis\`, \`y2Axis\` titles). Skip dual Y where unsupported (e.g. pie, funnel, scatter, table). If neither single nor dual Y fits the goal, try \`updateChartType\`, \`updateChartSorting\`, or \`updateChartQuery\` (limits)—only capabilities these tools expose.
  - **Many categories:** If cardinality looks high and rank or focus would help, consider \`updateChartSorting\` or \`updateChartQuery\` (Top/Bottom N)—only when warranted. For a **part-to-whole** view with **one category dimension and one measure**, \`updateChartType\` to **pie** can help when small slices roll up to an **"Other"** bucket.
  - **No data:** Empty or header-only \`dataset\` → tell the user and suggest refining the prompt.
  - Apply only when clearly warranted.

`;
}

/**
 * Standalone build chart engine - completely decoupled from any UI or store
 * @param params - The parameters for the build chart
 * @param context - All external dependencies injected
 * @returns The chart summary
 */
export async function buildChartEngine(
	params: z.infer<typeof buildChartSchema>,
	context: BuildChartContext,
): Promise<ChartSummary> {
	const { dataSourceTitle, userPrompt } = params;
	const effectiveQueryId = params.queryId ?? undefined;
	const effectivePriorChartId = params.priorChartId ?? undefined;

	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_BUILD_CHART),
		dataSourceTitle,
		userPrompt,
		effectiveQueryId,
		effectivePriorChartId,
	});

	if (!userPrompt) {
		throw new Error(
			'Error: Please ask the user to specify textual user input',
		);
	}

	// use the tool call id as the new chart id
	const newChartId = context.toolCallId;

	let savedChartWidgetProps: ExtendedChartWidgetProps | null = null;

	let message = '';
	let isError = false;

	const updatedContext: BuildChartContext = {
		...context,
		dataSourceTitle,
		naturalResponseEnabled: context.naturalResponseEnabled,
		retrieveChart: (chartId: string) => {
			// Prioritize savedChartWidgetProps in case of subsequent tool calls
			if (savedChartWidgetProps) {
				return savedChartWidgetProps;
			}
			if (chartId && context.retrieveChart) {
				return context.retrieveChart(chartId);
			}
			return null;
		},
		saveChart: (_, chartWidgetProps: ExtendedChartWidgetProps) => {
			// Always recompute chartState so it reflects the final widget state,
			// including changes applied by update subtools after the initial createChart.
			const chartTranslation = translateChartToJSON(chartWidgetProps);
			if (chartTranslation.success) {
				chartWidgetProps.chartState = {
					...chartTranslation.data,
					...(chartWidgetProps.title && {
						title: chartWidgetProps.title,
					}),
				};
			}
			context.saveChart?.(newChartId, chartWidgetProps);
			savedChartWidgetProps = chartWidgetProps;
		},
	};

	const targetedChartWidget =
		effectivePriorChartId && context.retrieveChart
			? context.retrieveChart(effectivePriorChartId)
			: null;

	const targetedChartSummary = targetedChartWidget
		? getBaseChartSummary(targetedChartWidget)
		: undefined;

	const chartsForPrompt: ChartContextPromptEntry[] =
		effectivePriorChartId !== undefined && effectivePriorChartId !== ''
			? targetedChartSummary
				? [
						{
							chartId: effectivePriorChartId,
							chartState: targetedChartSummary,
						},
				  ]
				: []
			: (context.chartSummaries ?? []).slice(-TOOL_CONTEXT_MAX_ASSETS);

	const systemPrompt = buildContextualSystemPrompt(
		chartsForPrompt,
		effectivePriorChartId,
		effectiveQueryId,
	);

	const internalTools = createChartTools(updatedContext);

	// Execute orchestration
	const { combinedMessage, isError: isErrorInToolsCompletion } =
		await createToolsCompletion({
			systemPrompt,
			userPrompt,
			tools: internalTools,
			openAIClient: context.openAIClient,
		});

	message = combinedMessage;
	isError = isErrorInToolsCompletion;

	if (isError) {
		throw new Error(message);
	}

	const dataset = datasetFromSavedChartWidget(
		context.naturalResponseEnabled,
		savedChartWidgetProps,
	);
	const queryState = queryStateFromSavedChartWidget(savedChartWidgetProps);

	return {
		message,
		createdAt: new Date().toISOString(),
		chartId: newChartId,
		dataSourceTitle,
		chartState: savedChartWidgetProps
			? getBaseChartSummary(savedChartWidgetProps)
			: undefined,
		queryState,
		...(dataset !== undefined && { dataset }),
	};
}
